import {
  Component, ElementRef,
  EventEmitter,
  forwardRef, Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges, ViewChild,
} from '@angular/core'
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR, ValidationErrors, ValidatorFn,
  Validators,
} from '@angular/forms'
import {
  ApiService,
  ChargeDescriptionMaster,
  ChargeDescriptionMasterData,
  Clinic,
  ClinicService,
  EstimateCharge,
  PermissionsService,
} from '@mmx/shared'
import { last } from 'lodash'
import { BehaviorSubject, combineLatest, map, Observable, startWith, Subject, switchMap, takeUntil } from 'rxjs'
import { combineLatestWith, debounceTime, distinctUntilChanged, shareReplay, tap } from 'rxjs/operators'

export interface ChargeRowFormGroup {
  chargeId: FormControl<string>
  chargeCode: FormControl<string>
  type: FormControl<ChargeDescriptionMaster.TYPE>
  description: FormControl<string>
  cptCode: FormControl<string>
  modifiers: FormControl<string>
  revCode: FormControl<string>
  quantity: FormControl<number>
  unitCharge: FormControl<number>
  totalCharge: FormControl<number>
  facilityId: FormControl<string>
  visitNumbers: FormControl<string>
}

@Component({
  selector: 'mmx-charges-table-form',
  templateUrl: './charges-table-form.component.html',
  styleUrls: ['./charges-table-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChargesTableFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: ChargesTableFormComponent,
      multi: true,
    },
  ],
  standalone: false,
})
export class ChargesTableFormComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @ViewChild('chargeList') chargeListElement: ElementRef
  @Input() facilityId: string
  @Output() valueUpdated = new EventEmitter()

  public displayedColumns = ['type', 'description', 'cptCode', 'modifiers', 'unit', 'actionsColumn']
  public ChargeDescriptionMaster = ChargeDescriptionMaster
  public clinic: Clinic
  public currentlyEditingIndex: number | null = null
  public chargesForm = this.fb.group({
    charges: this.fb.array<FormGroup<ChargeRowFormGroup>>([], Validators.required),
  })
  public filterDescCtrl = new FormControl()
  public filterCptCtrl = new FormControl()
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject(true)
  private changes$ = new Subject<void>()
  public filteredCdm$ = combineLatest([
    this.filterDescCtrl.valueChanges.pipe(startWith('')),
    this.filterCptCtrl.valueChanges.pipe(startWith('')),
    this.changes$.pipe(startWith(null)),
  ]).pipe(
    debounceTime(300),
    switchMap(([descValue, cptValue]) => {
      this.loading$.next(true)
      const filterValueDesc = (descValue || '').toUpperCase()
      const filterValueCpt = (cptValue || '').toUpperCase()
      let charge: FormGroup<ChargeRowFormGroup>

      if (this.currentlyEditingIndex !== null) {
        charge = this.charges.at(this.currentlyEditingIndex)
      } else {
        charge = last(this.charges.controls)
      }

      const type = charge.get('type').value
      const facilityId = charge.value.facilityId ?? this.facilityId
      const searchParams= {
        type,
        chargeDescription: filterValueDesc ? filterValueDesc : undefined,
        cptCode: filterValueCpt ? filterValueCpt : undefined,
        facilityId,
      }

      return this.restService.searchChargeDescriptionMaster(searchParams).pipe(
        map((cdm) => {
          // Generate a list of selectedIds, excluding the id of the charge being edited.
          const selectedIds = this.charges.controls
            .filter((_, index) => index !== this.currentlyEditingIndex) // Exclude the currently editing item
            .map(c => c.value.chargeId)
          const availableFilters = cdm.filter((item) => !selectedIds.includes(item.id))
          this.loading$.next(false)

          return availableFilters
        }),
      )
    }),
    shareReplay(1),
  )
  public total$ = this.chargesForm.valueChanges.pipe(
    startWith(this.chargesForm.value), // Start with initial form value
    map(() => this.calculateTotal()), // Recalculate total whenever form changes
    distinctUntilChanged(), // Emit only when total changes
  )

  private onDestroy$ = new Subject<void>()

  private facilities$ = this.clinicService.getLocations().pipe(
    takeUntil(this.onDestroy$),
    shareReplay(1),
  )

  hbFacility$ = this.facilities$.pipe(
    takeUntil(this.onDestroy$),
    combineLatestWith(this.changes$.pipe(
      takeUntil(this.onDestroy$),
      startWith(null),
    )),
    map(([facilities]) => facilities.find(f => f.id === this.facilityId)),
    shareReplay(1),
  )

  pbFacilityId: string

  pbFacilities$ = this.facilities$.pipe(
    takeUntil(this.onDestroy$),
    map(facilities => facilities.filter(facility => facility.mmx?.pbFacilityId != null)),
    tap(facilities => {
      if (facilities.length > 0) {
        this.pbFacilityId = facilities[0].id
      }
    }),
    shareReplay(1),
  )

  get charges() {
    return this.chargesForm.controls.charges
  }

  hbEnabled$: Observable<boolean> = this.clinicService.clinic$.pipe(
    takeUntil(this.onDestroy$),
    combineLatestWith(this.hbFacility$),
    map(([clinic, facility]) => clinic.pricer.hb && facility?.mmx?.hbFacilityId != null),
    distinctUntilChanged(),
    shareReplay(1),
  )

  pbEnabled$: Observable<boolean> = this.clinicService.clinic$.pipe(
    takeUntil(this.onDestroy$),
    map(clinic => clinic.pricer.pb),
    distinctUntilChanged(),
    shareReplay(1),
  )

  constructor(
    private fb: FormBuilder,
    private restService: ApiService,
    private clinicService: ClinicService,
    private permissionsService: PermissionsService,
  ) { }

  ngOnInit() {
    this.clinic = this.clinicService.clinic

    this.permissionsService.hasPermission('appointment.estimate.charge.view')
      .pipe(
        takeUntil(this.onDestroy$),
      ).subscribe(isAllow => {
        if (isAllow) {
          this.displayedColumns = ['type', 'description', 'cptCode', 'modifiers', 'revCode', 'unit', 'totalCharge', 'actionsColumn']
        }
      })

    this.chargesForm.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe(() => {
        this.charges.controls.forEach((_, index) => {
          this.calculateRowTotal(index)
        })
        return this.onChange(this.transformValue())
      })
  }

  onItemSelected(item: ChargeDescriptionMasterData, index: number) {
    const estimateItem = this.charges.at(index)
    if (estimateItem) {
      estimateItem.patchValue({
        chargeId: item.id,
        chargeCode: item.chargeCode,
        type: item.type,
        description: item.chargeDescription,
        cptCode: item.cptCode,
        modifiers: '',
        revCode: item.revCode,
        quantity: 1,
        unitCharge: item.unitCharge,
        totalCharge: 0,
      })
      this.currentlyEditingIndex = null
      this.disableControls(estimateItem, ['type', 'description', 'cptCode'])
      this.valueUpdated.emit()
    }
  }

  onItemTypeSelected(type: ChargeDescriptionMaster.TYPE, index: number) {
    const estimateItem = this.charges.at(index)
    if (estimateItem) {
      estimateItem.patchValue({
        type: type,
      })
      this.enableControls(estimateItem, ['description', 'cptCode'])
    }
    (this.filterDescCtrl.valueChanges as BehaviorSubject<string>).next('')
  }

  addEstimateItem(pricerType: ChargeDescriptionMaster.TYPE): void {
    this.filterDescCtrl.setValue(null)
    this.filterCptCtrl.setValue(null)

    const previousCharge = this.charges.at(this.charges.length - 1)

    const estimateItem = this.createEstimateItem({
      facilityId: pricerType === ChargeDescriptionMaster.TYPE.PB ? this.pbFacilityId : undefined,
      visitNumbers: previousCharge?.value?.visitNumbers ? this.parseVisitNumbers(previousCharge.value.visitNumbers) : [1],
    })

    this.charges.push(estimateItem)

    // If there's a last type selected, use it for the new item
    this.onItemTypeSelected(pricerType, this.charges.length - 1)

    // Use setTimeout to allow time for the DOM to update
    setTimeout(() => {
      const element = this.chargeListElement.nativeElement
      element.scrollTop = element.scrollHeight
    }, 200)
  }

  removeEstimateItem(index: number): void {
    this.charges.removeAt(index)
    this.valueUpdated.emit()
  }

  editEstimateItem(index: number): void {
    this.currentlyEditingIndex = index
    const estimateItem = this.charges.at(index)
    if (estimateItem) {
      this.onItemTypeSelected(estimateItem.get('type').value, index)
    }
  }

  cancelEditEstimateItem(index: number): void {
    this.currentlyEditingIndex = null
    const estimateItem = this.charges.at(index)
    if (estimateItem) {
      this.disableControls(estimateItem, ['type', 'description', 'cptCode'])
    }
  }

  public ngOnChanges({ }: SimpleChanges) {
    this.changes$.next()
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public writeValue(value: any) {
    if (value) {
      this.chargesForm.setControl('charges', this.createChargesFormArray(value))
    }
  }

  public validate() {
    return this.chargesForm.valid ? null : { valid: false }
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public onChange(_: any) { }
  public onTouched() { }

  private createChargesFormArray(charges: EstimateCharge[]): FormArray<FormGroup<ChargeRowFormGroup>> {
    const chargeFormArray = this.fb.array<FormGroup<ChargeRowFormGroup>>([])
    for (const charge of charges) {
      chargeFormArray.push(this.createEstimateItem(charge))
    }
    return chargeFormArray
  }

  private createEstimateItem(item?: Partial<EstimateCharge>): FormGroup<ChargeRowFormGroup> {
    return new FormGroup<ChargeRowFormGroup>({
      chargeId: new FormControl(item?.chargeId || ''),
      chargeCode: new FormControl(item?.chargeCode || ''),
      type: new FormControl({ value: item?.type, disabled: true }, Validators.required),
      description: new FormControl({ value: item?.description || '', disabled: true }, Validators.required),
      cptCode: new FormControl({ value: item?.cptCode || '', disabled: true }, Validators.required),
      modifiers: new FormControl(item?.modifiers || ''),
      revCode: new FormControl({ value: item?.revCode || '', disabled: true }),
      quantity: new FormControl(item?.quantity ?? 1, Validators.required),
      unitCharge: new FormControl({ value: item?.unitCharge || 0, disabled: true }),
      totalCharge: new FormControl({ value: item?.unitCharge || 0, disabled: true }),
      facilityId: new FormControl(item?.facilityId),
      visitNumbers: new FormControl(
        item?.visitNumbers ? item.visitNumbers.join(',') : '',
        [Validators.required, this.visitNumbersValidator],
      ),
    })
  }

  private disableControls(formGroup: FormGroup<ChargeRowFormGroup>, controlNames: string[]): void {
    controlNames.forEach(controlName => {
      const control = formGroup.get(controlName)
      if (control) {
        control.disable()
      }
    })
  }

  private enableControls(formGroup: FormGroup<ChargeRowFormGroup>, controlNames: string[]): void {
    controlNames.forEach(controlName => {
      const control = formGroup.get(controlName)
      if (control) {
        control.enable()
      }
    })
  }

  private calculateTotal(): number {
    return this.charges.controls.reduce((acc, cur) => {
      return acc + (cur.controls.unitCharge.value * cur.controls.quantity.value)
    }, 0)
  }

  private calculateRowTotal(index: number): void {
    const chargeItem = this.charges.at(index) as FormGroup
    const rowTotal = chargeItem.get('unitCharge').value * chargeItem.get('quantity').value
    chargeItem.get('totalCharge').setValue(rowTotal, { emitEvent: false }) // Prevent loop
  }

  private visitNumbersValidator(): ValidatorFn {
    return (formArray: AbstractControl): ValidationErrors | null => {
      if (!(formArray instanceof FormArray)) {
        return null
      }

      const visits = new Set<number>()

      for (const ctrl of formArray.controls) {
        const raw = ctrl.get('visitNumber')?.value
        const parsed = this.parseVisitNumbers(raw)
        parsed.forEach(v => visits.add(v))
      }

      const numbers = Array.from(visits).sort((a, b) => a - b)
      const isSequential = numbers.every((num, idx) => num === idx + 1)

      return !isSequential || numbers.length === 0 ? { visitNumber: true } : null
    }
  }

  private parseVisitNumbers(value: string): number[] {
    if (!value) {
      return []
    }
    const visits: number[] = []
    value.split(',').forEach(part => {
      const trimmed = part.trim()
      if (trimmed.includes('-')) {
        const [start, end] = trimmed.split('-').map(v => parseInt(v, 10))
        if (!isNaN(start) && !isNaN(end) && end >= start) {
          for (let i = start; i <= end; i++) {
            visits.push(i)
          }
        }
      } else {
        const n = parseInt(trimmed, 10)
        if (!isNaN(n)) {
          visits.push(n)
        }
      }
    })
    return visits
  }

  private transformValue(): Partial<EstimateCharge>[] {
    // we use getRawValue so it includes values for disabled controls
    return this.charges.getRawValue().map(charge => ({
      ...charge,
      visitNumbers: this.parseVisitNumbers(charge.visitNumbers),
    }))
  }
}
